export interface RealtimeEvent {
  type: 'event';
  event: string;
  channel: string;
  seq: string | null;
  data: Record<string, unknown>;
}

export interface RealtimeOptions {
  url: string;
  getAccessToken(): string | null;
  /** Called while the socket is down so screens fall back to polling (BUILD_PLAN §5.7: every 5 s). */
  onConnectionChange?(connected: boolean): void;
  WebSocketImpl?: typeof WebSocket;
}

type Listener = (e: RealtimeEvent) => void;

/**
 * WebSocket client for /v1/stream: reconnects with backoff, re-subscribes after reconnecting, and
 * tracks `seq` per channel so a listener learns when it missed updates (`gap`) and must refetch.
 */
export class RealtimeClient {
  private ws: WebSocket | null = null;
  private readonly listeners = new Map<string, Set<(e: RealtimeEvent, gap: boolean) => void>>();
  private readonly lastSeq = new Map<string, bigint>();
  /** Channels re-subscribed after a reconnect: their next event is reported as a gap. */
  private readonly resync = new Set<string>();
  private retry = 0;
  private timer: ReturnType<typeof setTimeout> | null = null;
  private stopped = false;
  private everConnected = false;
  connected = false;

  constructor(private readonly opts: RealtimeOptions) {}

  start(): void {
    this.stopped = false;
    this.open();
  }

  stop(): void {
    this.stopped = true;
    if (this.timer) clearTimeout(this.timer);
    this.ws?.close();
    this.ws = null;
  }

  /** Re-sends auth after sign-in so user:<id> channels work. */
  reauthenticate(): void {
    const token = this.opts.getAccessToken();
    if (token && this.connected) this.send({ op: 'auth', token });
  }

  subscribe(channel: string, listener: (e: RealtimeEvent, gap: boolean) => void): () => void {
    let set = this.listeners.get(channel);
    if (!set) {
      this.listeners.set(channel, (set = new Set()));
      if (this.connected) this.send({ op: 'sub', channel });
    }
    set.add(listener);
    return () => {
      set!.delete(listener);
      if (set!.size === 0) {
        this.listeners.delete(channel);
        this.lastSeq.delete(channel);
        this.resync.delete(channel);
        if (this.connected) this.send({ op: 'unsub', channel });
      }
    };
  }

  /** For tests and same-tab updates: deliver as if it came from the server. */
  deliver(e: RealtimeEvent): void {
    // Events may have been missed while the socket was down.
    let gap = this.resync.delete(e.channel);
    if (e.seq !== null) {
      const seq = BigInt(e.seq);
      const last = this.lastSeq.get(e.channel);
      // Room channels are coalesced (newest only); round and bingo channels expect consecutive seq.
      if (last !== undefined) {
        if (seq <= last) return; // stale or duplicate
        gap ||= /^(round|bingo):/.test(e.channel) && seq !== last + 1n;
      }
      this.lastSeq.set(e.channel, seq);
    }
    for (const l of this.listeners.get(e.channel) ?? []) l(e, gap);
  }

  private open(): void {
    const Impl = this.opts.WebSocketImpl ?? WebSocket;
    const ws = new Impl(this.opts.url);
    this.ws = ws;
    ws.onopen = () => {
      this.retry = 0;
      this.connected = true;
      this.opts.onConnectionChange?.(true);
      const token = this.opts.getAccessToken();
      if (token) this.send({ op: 'auth', token });
      const reconnect = this.everConnected;
      this.everConnected = true;
      for (const channel of this.listeners.keys()) {
        // After a reconnect anything may have been missed: the next event is treated as a gap.
        this.lastSeq.delete(channel);
        if (reconnect) this.resync.add(channel);
        this.send({ op: 'sub', channel });
      }
    };
    ws.onmessage = (m) => {
      try {
        const msg = JSON.parse(String(m.data)) as { type: string };
        if (msg.type === 'event') this.deliver(msg as RealtimeEvent);
        else if (msg.type === 'authenticated') this.resubscribeUserChannels();
      } catch {
        /* ignore malformed frames */
      }
    };
    ws.onclose = () => {
      if (this.connected) this.opts.onConnectionChange?.(false);
      this.connected = false;
      if (this.stopped) return;
      const delay = Math.min(30_000, 500 * 2 ** this.retry++) * (0.5 + Math.random() / 2);
      this.timer = setTimeout(() => this.open(), delay);
    };
  }

  private resubscribeUserChannels(): void {
    for (const channel of this.listeners.keys()) if (channel.startsWith('user:')) this.send({ op: 'sub', channel });
  }

  private send(body: unknown): void {
    if (this.ws?.readyState === 1) this.ws.send(JSON.stringify(body));
  }
}
